<template>
  <div class="card border-0 shadow-none rounded-3 mb-4">
    <div class="card-body p-4 p-xl-40">
      <div class="toolbar-card mb-4">
        <div class="toolbar-left">
          <h4 class="mb-1">{{ t("income_page.income_list") }}</h4>
          <p class="text-muted mb-0">{{ t("income_page.toolbar_description") }}</p>
        </div>
        <div class="toolbar-right">
          <div class="search-shell">
            <input
              :value="searchQuery"
              type="text"
              class="form-control toolbar-search"
              :placeholder="t('income_page.search_placeholder')"
              @input="$emit('update:searchQuery', ($event.target as HTMLInputElement).value)"
            />
            <i class="ri-search-line search-icon"></i>
          </div>
          <router-link to="/create-income" class="btn style-one btn-sm d-flex align-items-center gap-2">
            <i class="ri-add-circle-line"></i>
            <span>{{ t("create_income") }}</span>
          </router-link>
        </div>
      </div>

      <a-table :columns="columns" :data-source="incomes" :row-key="'id'" :loading="loading" :pagination="paginationConfig" :locale="tableLocale" class="income-table">
        <template #bodyCell="{ column, record, text }">
          <template v-if="column.key === 'ref'">
            <span class="code-pill">{{ record.ref || t("income_page.not_available") }}</span>
          </template>
          <template v-else-if="column.key === 'date'">
            {{ formatDate(record.date) }}
          </template>
          <template v-else-if="column.key === 'warehouse'">
            {{ record.warehouse?.name || record.warehouse_name || t("income_page.not_available") }}
          </template>
          <template v-else-if="column.key === 'category'">
            {{ record.category?.name || record.category_name || t("income_page.not_available") }}
          </template>
          <template v-else-if="column.key === 'amount'">
            ${{ Number(record.amount || 0).toFixed(2) }}
          </template>
          <template v-else-if="column.key === 'status'">
            <span class="status-badge" :class="Number(record.status ?? 1) === 1 ? 'status-active' : 'status-inactive'">
              {{ Number(record.status ?? 1) === 1 ? t("income_page.active") : t("income_page.inactive") }}
            </span>
          </template>
          <template v-else-if="column.key === 'actions'">
            <div class="d-flex justify-content-center gap-2">
              <router-link :to="`/edit-income/${record.id}`" class="icon-btn" :title="t('income_page.edit')">
                <i class="ri-pencil-line"></i>
              </router-link>
              <button class="icon-btn danger" :title="t('income_page.delete')" @click="$emit('delete', record)">
                <i class="ri-delete-bin-6-line"></i>
              </button>
            </div>
          </template>
          <template v-else>
            {{ text || t("income_page.not_available") }}
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";
import { useI18n } from "vue-i18n";
import type { Income } from "@/services/income";

export default defineComponent({
  name: "IncomeList",
  props: {
    incomes: { type: Array as PropType<Income[]>, required: true },
    searchQuery: { type: String, default: "" },
    loading: { type: Boolean, default: false },
  },
  emits: ["update:searchQuery", "delete"],
  setup() {
    const { t, locale } = useI18n();
    const columns = computed(() => [
      { title: t("income_page.date"), key: "date", dataIndex: "date", align: "center" as const, width: 220 },
      { title: t("income_page.reference"), key: "ref", dataIndex: "ref", align: "center" as const, width: 160 },
      { title: t("income_page.warehouse"), key: "warehouse", align: "center" as const, width: 180 },
      { title: t("income_page.category"), key: "category", align: "center" as const, width: 180 },
      { title: t("income_page.amount"), key: "amount", dataIndex: "amount", align: "center" as const, width: 140 },
      { title: t("income_page.status"), key: "status", dataIndex: "status", align: "center" as const, width: 120 },
      { title: t("income_page.action"), key: "actions", align: "center" as const, width: 120 },
    ]);
    const tableLocale = computed(() => ({
      emptyText: t("income_page.no_incomes_found"),
    }));
    const paginationConfig = computed(() => ({
      showSizeChanger: true,
      pageSizeOptions: ["10", "25", "50", "100"],
      defaultPageSize: 10,
      locale: { items_per_page: t("income_page.items_per_page") },
    }));
    const formatDate = (value?: string) =>
      value
        ? new Date(value).toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", { timeZone: "Asia/Phnom_Penh" })
        : t("income_page.not_available");

    return { columns, paginationConfig, tableLocale, formatDate, t };
  },
});
</script>

<style scoped>
.toolbar-card { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 18px; padding: 22px; border: 1px solid #edf0f7; border-radius: 20px; background: #fbfcff; }
.toolbar-right { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; }
.search-shell { position: relative; min-width: 340px; }
.toolbar-search { min-height: 54px; padding: 14px 48px 14px 18px; border-radius: 16px; border: 1px solid #d7def4; box-shadow: none; }
.toolbar-search:focus { border-color: #605dff; box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.12); }
.search-icon { position: absolute; right: 18px; top: 50%; transform: translateY(-50%); color: #7d8aa2; pointer-events: none; }
.code-pill { display: inline-flex; align-items: center; justify-content: center; min-width: 88px; padding: 6px 12px; border-radius: 999px; background: #eef2ff; color: #4f46e5; font-weight: 700; }
.status-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 88px; padding: 6px 12px; border-radius: 999px; font-size: 12px; font-weight: 700; }
.status-active { background: #e8f7ee; color: #1f9254; }
.status-inactive { background: #fdecec; color: #d33c43; }
.icon-btn { display: inline-flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 10px; border: 1px solid #e3e8f5; background: #fff; color: #4f46e5; }
.icon-btn.danger { color: #d33c43; }
</style>
